import { playSample } from './audio'
import { FRAME_SECONDS } from './constants'
import { GOAL_SUCCESS_SFX } from './missionSfx'
import { concealServerStorageTemplate } from './primitives/serverStorageTemplate'
import {
  findStateByName,
  matchesTemplateOrClone,
  spawnProgrammedClone,
  touchesAvatar,
} from './sharedHelpers'
import { BehaviorHandler, MissionBehaviors, HudStyle } from './types'

/**
 * RJF09（M9 ボール集め）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF09.rbxl）のギミックを移植する:
 * - ServerScriptService/CloneScript: プレイ開始 10 秒後に、①ボールを
 *   ④ボールのかず.Position.X 個、②トラップを ⑤トラップのかず.Position.X 個
 *   クローンして配置する（個数は学習者がパーツを動かして決める）。
 *   同時にタイム計測を開始する。
 * - ①ボール/WhiteBallScript: キャラクターが触れるとボール 1 個ぶん回収
 *   （ボールごとに 1 回だけ）。残数が 0 になったらクリア音 +
 *   「ゲームクリア！◯びょう だったよ！」を表示（StarterGui/LocalScript）。
 *   Y < -30 へ落ちたら (0,130,0) へ戻す。
 * - ②トラップ/TrapScript: Y < -30 へ落ちたら (0,130,0) へ戻す（接触処理は
 *   なく、学習者がプログラムで動かす障害物）。
 *
 * プレビューでの近似・意図的な差異:
 * - leaderstats（プレイヤーリストの「ボールのかず」）は HUD テキストの
 *   残数表示で代替する。実機どおり開始約 1 秒後（LeaderboardScript の
 *   wait(1)）から、クローン配置前でも表示する。
 * - ボールの接触は AABB 重なりの毎フレーム監視で判定する（クローンごとに
 *   「一度だけ回収」を正確に再現するため。enter エッジの Touched 相当）。
 *   実機の WhiteBallScript は Clone() でコピーされ全クローンで動くため、
 *   環境配置分だけでなく学習者が control_clone で増やしたボールも対象にする
 *   （名前 ①ボール のオブジェクトを毎フレーム走査する）。
 * - 残数 0 のクリア判定は実機の leaderstats 減算と同じ「初期数 − 接触済み数」。
 *   実機はクローン配置（10 秒）前に FinishEvent が発火すると GUI 接続前で
 *   表示されないが、これは再現しない（配置前に全回収できた場合もクリアを
 *   表示する。経過秒は配置前なら 0 とする）。
 * - クリア音（Spinning Around (b)）は実音源（GOAL_SUCCESS_SFX）で鳴らす。
 */

/** クローン配置までの待機秒数（Lua: wait(10)）。 */
const SETUP_DELAY_SECONDS = 10

/** leaderstats（ボールのかず）の表示開始秒数（Lua: LeaderboardScript の wait(1)）。 */
const LEADERSTATS_DELAY_SECONDS = 1

/** 落下リセットの高さしきい値（Lua: Position.Y < -30）。 */
const FALL_THRESHOLD_Y = -30

/** 落下リセット先（Lua: Vector3.new(0,130,0)）。 */
const FALL_RESPAWN_POSITION = { x: 0, y: 130, z: 0 }

/** テンプレート・かず指定パーツのオブジェクト名。 */
const BALL_TEMPLATE_NAME = '①ボール'
const TRAP_TEMPLATE_NAME = '②トラップ'
const BALL_COUNT_NAME = '④ボールのかず'
const TRAP_COUNT_NAME = '⑤トラップのかず'

/**
 * 「ボールのかず」の表示スタイル。実機ではプレイヤーリスト（leaderstats、
 * 画面右上の半透明黒）に出る値で、ScreenGui ではないためその印象に合わせる。
 * プレビューの右上は出力ログ（OutputLog）が使うため、カウント類は左上へ
 * 寄せる。
 */
const LEADERSTATS_HUD_STYLE: HudStyle = {
  background: 'rgba(0, 0, 0, 0.45)',
  color: '#FFFFFF',
  left: 8,
  top: 8,
}

/** ゲーム GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const BALL_HUD_STYLE: HudStyle = {
  background: '#FFFFFF',
  color: '#0055FF',
  border: '#0055FF',
  borderWidth: 5,
  wide: true,
  top: '80%',
}

export const createRjf09Behaviors = (): MissionBehaviors => {
  /**
   * ミッション本体（CloneScript + WhiteBallScript + TrapScript +
   * StarterGui/LocalScript の統合）。
   */
  const runMission: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    const ballTemplate = findStateByName(world, BALL_TEMPLATE_NAME)
    const trapTemplate = findStateByName(world, TRAP_TEMPLATE_NAME)
    if (!ballTemplate || !trapTemplate) {
      return
    }
    // クローンの複製元はこの時点のテンプレート状態（実機の ServerStorage に
    // 保存された状態に相当）。テンプレート自身に付いた学習者プログラムが
    // 後から動かしても、クローンには影響しない。
    const ballSnapshot = { ...ballTemplate }
    const trapSnapshot = { ...trapTemplate }
    // 実機の ServerStorage 同様、テンプレート本体はプレイ中隠す。
    for (const templateState of [ballTemplate, trapTemplate]) {
      concealServerStorageTemplate(world, templateState.id)
    }
    const templateIds = new Set([ballTemplate.id, trapTemplate.id])
    // 個数 = かず指定パーツの X 座標（学習者がパーツを動かして決める）。
    // 実機は LeaderboardScript が 1 秒時点、CloneScript が 10 秒時点で
    // それぞれ読むため、ここでは読まずに各時点で読む。
    const readCount = (name: string): number =>
      Math.max(0, Math.floor(findStateByName(world, name)?.position.x ?? 0))

    // 回収済みボール（Lua: 各ボールの changed フラグ）。残数は実機の
    // leaderstats と同じ「初期値（1 秒時点の ④ の X 座標）− 接触済み数」
    // （接触したボールがどの経路で生まれたクローンかは問わない）。
    const touchedBallIds = new Set<string>()
    // leaderstats の初期値。実機どおり 1 秒時点で読む（null = 作成前）。
    let leaderstatsCount: number | null = null
    let cleared = false
    // タイム計測の起点。Lua の CloneScript は wait(10) の直後・クローン配置の
    // 前に `local timer = time()` を記録する（配置前のクリアは経過 0 扱い）。
    let cloneStartMs: number | null = null
    let clonesSpawned = false
    // 経過はフレーム数で数える（wait(FRAME_SECONDS) 駆動。実時間の浮動小数
    // 累積より決定的で、他ビヘイビアの wait ベースのタイミングとも揃う）。
    let frame = 0
    const leaderstatsDelayFrames = Math.round(
      LEADERSTATS_DELAY_SECONDS / FRAME_SECONDS,
    )
    const setupDelayFrames = Math.round(SETUP_DELAY_SECONDS / FRAME_SECONDS)

    while (!ctx.signal.aborted) {
      // Lua: LeaderboardScript は参加 1 秒後に ④ の X 座標を読んで
      // leaderstats を作る（それまでに学習者がパーツを動かせば値も変わる）。
      let leaderstatsShownThisFrame = false
      if (
        leaderstatsCount === null &&
        !cleared &&
        frame >= leaderstatsDelayFrames
      ) {
        leaderstatsCount = readCount(BALL_COUNT_NAME)
        leaderstatsShownThisFrame = true
        ctx.setHudText?.(
          `ボールのかず: ${leaderstatsCount - touchedBallIds.size}`,
          LEADERSTATS_HUD_STYLE,
        )
      }
      // Lua: CloneScript は wait(10) 後に ④/⑤ の X 座標を読んでボール・
      // トラップを配置する。Roblox の Clone() は配下の Script ごと複製する
      // ため、クローンにも学習者が ①ボール/②トラップ に付けたプログラムを
      // 起動する（乱数で散らばる・触れたら消える等はこのプログラムが担う）。
      if (!clonesSpawned && frame >= setupDelayFrames) {
        clonesSpawned = true
        cloneStartMs = performance.now()
        const ballCount = readCount(BALL_COUNT_NAME)
        const trapCount = readCount(TRAP_COUNT_NAME)
        for (let index = 0; index < ballCount; index += 1) {
          spawnProgrammedClone(ctx, ballSnapshot, 'rjf09_ball')
        }
        for (let index = 0; index < trapCount; index += 1) {
          spawnProgrammedClone(ctx, trapSnapshot, 'rjf09_trap')
        }
      }

      // ボール・トラップを名前で走査する（環境配置分 + 学習者の control_clone
      // 分「元名#cloneN」の両方が対象。テンプレート本体は ServerStorage 相当
      // なので除く）。
      let touchedThisFrame = 0
      for (const state of world.getState().values()) {
        if (templateIds.has(state.id)) {
          continue
        }
        if (matchesTemplateOrClone(state.name, BALL_TEMPLATE_NAME)) {
          if (
            !cleared &&
            !touchedBallIds.has(state.id) &&
            touchesAvatar(world, state.position, state.size)
          ) {
            // Lua: WhiteBallScript の changed フラグ（ボールごとに 1 回だけ）。
            touchedBallIds.add(state.id)
            touchedThisFrame += 1
          }
          // 落下リセット（Lua: WhiteBallScript の while ループ）。
          if (state.position.y < FALL_THRESHOLD_Y) {
            world.setPosition(state.id, { ...FALL_RESPAWN_POSITION })
          }
        } else if (matchesTemplateOrClone(state.name, TRAP_TEMPLATE_NAME)) {
          // 落下リセット（Lua: TrapScript）。
          if (state.position.y < FALL_THRESHOLD_Y) {
            world.setPosition(state.id, { ...FALL_RESPAWN_POSITION })
          }
        }
      }
      // Lua: WhiteBallScript は減算したときだけクリア条件を見る（初期数 0 でも
      // 接触なしで勝手にクリアにはならない）。leaderstats 表示前の接触ぶんは
      // 表示時点でまとめて反映する（実機は leaderstats 不在中の接触で減算
      // できないが、配置前に全回収した場合もクリアできるようにする）。
      if (
        !cleared &&
        leaderstatsCount !== null &&
        (touchedThisFrame > 0 || leaderstatsShownThisFrame)
      ) {
        const remaining = leaderstatsCount - touchedBallIds.size
        if (remaining <= 0 && touchedBallIds.size > 0) {
          cleared = true
          playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
          const elapsedSeconds =
            cloneStartMs === null ? 0 : (
              Math.floor((performance.now() - cloneStartMs) / 1000)
            )
          ctx.setHudText?.(
            `ゲームクリア！${elapsedSeconds}びょう だったよ！`,
            BALL_HUD_STYLE,
          )
        } else if (touchedThisFrame > 0) {
          ctx.setHudText?.(`ボールのかず: ${remaining}`, LEADERSTATS_HUD_STYLE)
        }
      }
      await ctx.wait(FRAME_SECONDS)
      frame += 1
    }
  }

  return {
    onPlay: [runMission],
    // ①ボール/②トラップ は実機では ServerStorage（Script 非実行）。本体では
    // プログラムを起動せず、クローンにだけ startObjectPrograms で起動する。
    serverStorageTemplates: [BALL_TEMPLATE_NAME, TRAP_TEMPLATE_NAME],
  }
}
